import React from 'react';
import { Box, Card, CardContent, Typography, useTheme, useMediaQuery } from '@mui/material';
import {
  BarChart,
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  Cell,
} from 'recharts';

interface WeeklyItem {
  week_start: string;
  week_end: string;
  amount: number;
  count: number;
  label: string;
}

interface MonthlyItem {
  month: string;
  label: string;
  amount: number;
  change_pct?: number;
}

interface SalesWeeklyItem extends WeeklyItem {
  credit_amount: number;
  product_amount: number;
  credit_count: number;
  product_count: number;
  product_cost: number;
}

interface SalesMonthlyItem extends MonthlyItem {
  credit_amount: number;
  product_amount: number;
  credit_count: number;
  product_count: number;
  product_cost: number;
}

interface Props {
  weeklyHistory: WeeklyItem[];
  monthlyHistory: MonthlyItem[];
  weeklySalesHistory: SalesWeeklyItem[];
  monthlySalesHistory: SalesMonthlyItem[];
  onMonthClick?: (month: string, label: string) => void;
}

function fmtShort(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}k`;
  return n.toString();
}

function fmtCurrency(n: number): string {
  return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS', maximumFractionDigits: 0 }).format(n);
}

const CustomTooltip = ({ active, payload, label, countLabel = 'cobros' }: any) => {
  if (!active || !payload?.length) return null;
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, boxShadow: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" fontWeight={600}>{label}</Typography>
      <Typography variant="body2">{fmtCurrency(payload[0].value)}</Typography>
      {payload[0].payload.count !== undefined && (
        <Typography variant="caption" color="text.secondary">{payload[0].payload.count} {countLabel}</Typography>
      )}
      {payload[0].payload.change_pct !== undefined && payload[0].payload.change_pct !== 0 && (
        <Typography variant="caption" color={payload[0].payload.change_pct > 0 ? 'success.main' : 'error.main'}>
          {payload[0].payload.change_pct > 0 ? '+' : ''}{payload[0].payload.change_pct}% vs anterior
        </Typography>
      )}
    </Box>
  );
};

const SalesTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  const total = (item.credit_amount ?? 0) + (item.product_amount ?? 0);
  const cost = item.product_cost ?? 0;
  const margin = (item.product_amount ?? 0) - cost;
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, boxShadow: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" fontWeight={600}>{label}</Typography>
      <Typography variant="body2">{fmtCurrency(total)}</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Créditos: {fmtCurrency(item.credit_amount ?? 0)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Productos: {fmtCurrency(item.product_amount ?? 0)}
      </Typography>
      <Typography variant="caption" color="error.main" sx={{ display: 'block' }}>
        Costo mercadería: {fmtCurrency(cost)}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Margen productos: {fmtCurrency(margin)}
      </Typography>
      {item.count !== undefined && (
        <Typography variant="caption" color="text.secondary">{item.count} ventas</Typography>
      )}
      {item.change_pct !== undefined && item.change_pct !== 0 && (
        <Typography variant="caption" color={item.change_pct > 0 ? 'success.main' : 'error.main'} sx={{ display: 'block' }}>
          {item.change_pct > 0 ? '+' : ''}{item.change_pct}% vs anterior
        </Typography>
      )}
    </Box>
  );
};

const SalesCountTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  const total = (item.credit_count ?? 0) + (item.product_count ?? 0);
  return (
    <Box sx={{ bgcolor: 'background.paper', p: 1.5, borderRadius: 1, boxShadow: 2, border: '1px solid', borderColor: 'divider' }}>
      <Typography variant="caption" fontWeight={600}>{label}</Typography>
      <Typography variant="body2">{total} ventas</Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Créditos: {item.credit_count ?? 0}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        Productos: {item.product_count ?? 0}
      </Typography>
    </Box>
  );
};

export default function FinanceHistoryCharts({
  weeklyHistory,
  monthlyHistory,
  weeklySalesHistory,
  monthlySalesHistory,
  onMonthClick,
}: Props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const barColor = theme.palette.mode === 'dark' ? '#90caf9' : '#1976d2';
  const barColorMonth = theme.palette.mode === 'dark' ? '#ce93d8' : '#7b1fa2';
  const barColorSalesWeek = theme.palette.mode === 'dark' ? '#80cbc4' : '#00897b';
  const barColorSalesMonth = theme.palette.mode === 'dark' ? '#ffcc80' : '#ef6c00';

  const gridSx = {
    display: 'grid',
    gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
    gap: 2,
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Box sx={gridSx}>
        {/* Weekly collections chart */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Cobranzas Semanales
            </Typography>
            <Box sx={{ width: '100%', height: 250 }}>
              <ResponsiveContainer>
                <BarChart data={weeklyHistory} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={45} />
                  <Tooltip content={<CustomTooltip countLabel="cobros" />} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {weeklyHistory.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={index === weeklyHistory.length - 1 ? '#4caf50' : barColor}
                        opacity={index === weeklyHistory.length - 1 ? 1 : 0.7}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Monthly collections chart */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Cobranzas Mensuales
            </Typography>
            <Box sx={{ width: '100%', height: 250 }}>
              <ResponsiveContainer>
                <BarChart data={monthlyHistory} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={45} />
                  <Tooltip content={<CustomTooltip countLabel="cobros" />} />
                  <Bar dataKey="amount" radius={[4, 4, 0, 0]}>
                    {monthlyHistory.map((entry, index) => (
                      <Cell
                        key={index}
                        fill={index === monthlyHistory.length - 1 ? '#4caf50' : barColorMonth}
                        opacity={index === monthlyHistory.length - 1 ? 1 : 0.7}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={gridSx}>
        {/* Weekly sales chart */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Ventas Semanales
            </Typography>
            <Box sx={{ width: '100%', height: 270 }}>
              <ResponsiveContainer>
                <ComposedChart data={weeklySalesHistory} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={45} />
                  <Tooltip content={<SalesTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="credit_amount" name="Créditos" stackId="sales" fill={barColorSalesWeek} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="product_amount" name="Productos" stackId="sales" fill={barColorSalesMonth} radius={[4, 4, 0, 0]} />
                  <Line dataKey="product_cost" name="Costo mercadería" stroke={theme.palette.error.main} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Monthly sales chart */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Ventas Mensuales {onMonthClick && <Typography component="span" variant="caption" color="text.secondary">(click en un mes para ver el detalle)</Typography>}
            </Typography>
            <Box sx={{ width: '100%', height: 270 }}>
              <ResponsiveContainer>
                <ComposedChart data={monthlySalesHistory} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={fmtShort} tick={{ fontSize: 11 }} width={45} />
                  <Tooltip content={<SalesTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar
                    dataKey="credit_amount"
                    name="Créditos"
                    stackId="sales"
                    fill={barColorSalesWeek}
                    radius={[0, 0, 0, 0]}
                    cursor={onMonthClick ? 'pointer' : undefined}
                    onClick={(d: any) => onMonthClick?.(d.payload.month, d.payload.label)}
                  />
                  <Bar
                    dataKey="product_amount"
                    name="Productos"
                    stackId="sales"
                    fill={barColorSalesMonth}
                    radius={[4, 4, 0, 0]}
                    cursor={onMonthClick ? 'pointer' : undefined}
                    onClick={(d: any) => onMonthClick?.(d.payload.month, d.payload.label)}
                  />
                  <Line dataKey="product_cost" name="Costo mercadería" stroke={theme.palette.error.main} strokeWidth={2} dot={{ r: 3 }} />
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>

      <Box sx={gridSx}>
        {/* Weekly sales count chart */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Cantidad de Ventas Semanales
            </Typography>
            <Box sx={{ width: '100%', height: 270 }}>
              <ResponsiveContainer>
                <BarChart data={weeklySalesHistory} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                  <Tooltip content={<SalesCountTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar dataKey="credit_count" name="Créditos" stackId="salesCount" fill={barColorSalesWeek} radius={[0, 0, 0, 0]} />
                  <Bar dataKey="product_count" name="Productos" stackId="salesCount" fill={barColorSalesMonth} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Monthly sales count chart */}
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Cantidad de Ventas Mensuales {onMonthClick && <Typography component="span" variant="caption" color="text.secondary">(click en un mes para ver el detalle)</Typography>}
            </Typography>
            <Box sx={{ width: '100%', height: 270 }}>
              <ResponsiveContainer>
                <BarChart data={monthlySalesHistory} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                  <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={30} />
                  <Tooltip content={<SalesCountTooltip />} />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                  <Bar
                    dataKey="credit_count"
                    name="Créditos"
                    stackId="salesCount"
                    fill={barColorSalesWeek}
                    radius={[0, 0, 0, 0]}
                    cursor={onMonthClick ? 'pointer' : undefined}
                    onClick={(d: any) => onMonthClick?.(d.payload.month, d.payload.label)}
                  />
                  <Bar
                    dataKey="product_count"
                    name="Productos"
                    stackId="salesCount"
                    fill={barColorSalesMonth}
                    radius={[4, 4, 0, 0]}
                    cursor={onMonthClick ? 'pointer' : undefined}
                    onClick={(d: any) => onMonthClick?.(d.payload.month, d.payload.label)}
                  />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
